import { networkUrl } from "@/lib/site";
import { useId } from "react";
import { Link } from "react-router-dom";
import type { AnxietyMapData, MapNode } from "@/lib/maps";
import { mapAltText } from "@/lib/maps";

// Greedily wrap a label into up to three short lines for the SVG node circles.
function wrapLabel(label: string, maxChars = 13, maxLines = 3): string[] {
  const words = label.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1].replace(/\s+\S*$/, "") + "...";
    return kept;
  }
  return lines;
}

const VIEW = 820;
const CENTER = VIEW / 2;
const RING = 300; // distance from center to each outer node
const OUTER_R = 78; // outer node radius
const CENTRAL_R = 92; // central node radius

function nodePosition(index: number, total: number) {
  const angle = (-90 + index * (360 / total)) * (Math.PI / 180);
  return {
    x: CENTER + RING * Math.cos(angle),
    y: CENTER + RING * Math.sin(angle),
  };
}

function isExternal(node: MapNode): boolean {
  return Boolean(node.external) || node.href.startsWith("http");
}

export default function AnxietyMap({ map }: { map: AnxietyMapData }) {
  const styleId = useId().replace(/[:]/g, "");
  const alt = mapAltText(map);

  return (
    <div data-testid={`anxiety-map-${map.slug}`}>
      <style>{`
        .${styleId} .amap-line {
          stroke: hsl(var(--border));
          stroke-width: 2;
          transition: stroke 160ms ease;
        }
        .${styleId} .amap-node-circle {
          fill: hsl(var(--card));
          stroke: hsl(var(--border));
          stroke-width: 1.5;
          transition: fill 160ms ease, stroke 160ms ease;
        }
        .${styleId} .amap-node-text {
          fill: hsl(var(--foreground));
          transition: fill 160ms ease;
        }
        .${styleId} .amap-node:hover .amap-node-circle,
        .${styleId} .amap-node:focus-visible .amap-node-circle {
          fill: var(--brand-cta-bg, hsl(var(--brand) / 0.12));
          stroke: hsl(var(--brand));
          stroke-width: 2.5;
        }
        .${styleId} .amap-node:hover .amap-node-text,
        .${styleId} .amap-node:focus-visible .amap-node-text {
          fill: hsl(var(--brand-text));
        }
        .${styleId} .amap-node:hover ~ .amap-line { stroke: hsl(var(--brand)); }
        .${styleId} .amap-node { cursor: pointer; outline: none; }
      `}</style>

      {/* Desktop: hand-laid radial SVG */}
      <div className="hidden md:block">
        <svg
          className={styleId}
          viewBox={`0 0 ${VIEW} ${VIEW}`}
          role="img"
          aria-label={alt}
          style={{ width: "100%", height: "auto", maxWidth: 760, margin: "0 auto", display: "block" }}
        >
          {/* spokes first so nodes paint on top */}
          {map.nodes.map((node, i) => {
            const { x, y } = nodePosition(i, map.nodes.length);
            return (
              <line
                key={`line-${i}`}
                className="amap-line"
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
              />
            );
          })}

          {/* central node */}
          <g aria-hidden="true">
            <circle
              cx={CENTER}
              cy={CENTER}
              r={CENTRAL_R}
              fill="hsl(var(--brand))"
            />
            {wrapLabel(map.central, 11, 2).map((line, li, arr) => (
              <text
                key={`c-${li}`}
                x={CENTER}
                y={CENTER + (li - (arr.length - 1) / 2) * 26 + 7}
                textAnchor="middle"
                fontFamily="var(--font-serif, Georgia, serif)"
                fontSize="26"
                fontWeight={600}
                fill="#ffffff"
              >
                {line}
              </text>
            ))}
          </g>

          {/* outer nodes */}
          {map.nodes.map((node, i) => {
            const { x, y } = nodePosition(i, map.nodes.length);
            const lines = wrapLabel(node.label);
            const external = isExternal(node);
            return (
              <a
                key={`node-${i}`}
                className="amap-node"
                href={node.href ? networkUrl(node.href, "anxiety_map") : undefined}
                aria-label={`${node.label}: ${node.desc}`}
                {...(external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                <circle className="amap-node-circle" cx={x} cy={y} r={OUTER_R} />
                {lines.map((line, li, arr) => (
                  <text
                    key={`t-${i}-${li}`}
                    className="amap-node-text"
                    x={x}
                    y={y + (li - (arr.length - 1) / 2) * 17 + 5}
                    textAnchor="middle"
                    fontSize="14.5"
                    fontWeight={500}
                  >
                    {line}
                  </text>
                ))}
              </a>
            );
          })}
        </svg>
      </div>

      {/* Mobile: single-column list, central concept on top */}
      <div className="md:hidden">
        <div
          className="text-center font-serif text-xl font-semibold mx-auto mb-4 px-6 py-4"
          style={{
            background: "hsl(var(--brand))",
            color: "#ffffff",
            borderRadius: "var(--radius)",
            maxWidth: 320,
          }}
        >
          {map.central}
        </div>
        <ul className="list-none p-0 m-0 space-y-3">
          {map.nodes.map((node, i) => {
            const external = isExternal(node);
            const inner = (
              <>
                <span
                  className="block font-serif text-base font-medium"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {node.label}
                </span>
                <span
                  className="block text-sm leading-snug mt-1"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {node.desc}
                </span>
              </>
            );
            const cardStyle = {
              border: "1px solid hsl(var(--border))",
              borderLeft: "3px solid hsl(var(--brand))",
              borderRadius: "var(--radius)",
              background: "hsl(var(--card))",
            } as const;
            return (
              <li key={`m-${i}`}>
                {external ? (
                  <a
                    href={node.href ? networkUrl(node.href, "anxiety_map") : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 hover:no-underline"
                    style={cardStyle}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link
                    to={node.href}
                    className="block p-4 hover:no-underline"
                    style={cardStyle}
                  >
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// Preview card shared by the hub page and the homepage Maps section.
export function MapCard({ map }: { map: AnxietyMapData }) {
  return (
    <Link
      to={`/maps/${map.slug}`}
      className="group card-lift block bg-card p-6 hover:no-underline"
      style={{
        border: "1px solid hsl(var(--border))",
        borderTop: "3px solid hsl(var(--brand))",
        borderRadius: "var(--radius)",
      }}
      data-testid={`map-card-${map.slug}`}
    >
      <div className="flex items-center gap-4">
        <div style={{ flex: "0 0 auto" }}>
          <MapPreview map={map} />
        </div>
        <h3 className="font-serif text-lg text-foreground" style={{ margin: 0 }}>
          {map.name}
        </h3>
      </div>
      <p
        className="mt-3 text-sm leading-relaxed"
        style={{ color: "hsl(var(--muted-foreground))" }}
      >
        {map.previewDesc}
      </p>
      <span
        className="inline-block mt-4 text-xs font-semibold"
        style={{ color: "hsl(var(--brand-text))" }}
      >
        Open the {map.name}{" "}
        <span className="link-arrow" aria-hidden="true">
          &rarr;
        </span>
      </span>
    </Link>
  );
}

// Decorative simplified preview used on the hub and homepage cards. Purely
// visual (aria-hidden); the card's title and description carry the meaning.
export function MapPreview({
  map,
  maxWidth = 120,
}: {
  map: AnxietyMapData;
  maxWidth?: number;
}) {
  const size = 120;
  const c = size / 2;
  const ring = 42;
  const spokes = Math.min(map.nodes.length, 6);
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      focusable="false"
      style={{ width: "100%", height: "auto", maxWidth }}
    >
      {Array.from({ length: spokes }).map((_, i) => {
        const angle = (-90 + i * (360 / spokes)) * (Math.PI / 180);
        const x = c + ring * Math.cos(angle);
        const y = c + ring * Math.sin(angle);
        return (
          <g key={i}>
            <line
              x1={c}
              y1={c}
              x2={x}
              y2={y}
              stroke="hsl(var(--border))"
              strokeWidth={1.5}
            />
            <circle
              cx={x}
              cy={y}
              r={9}
              fill="hsl(var(--card))"
              stroke="hsl(var(--border))"
              strokeWidth={1.25}
            />
          </g>
        );
      })}
      <circle cx={c} cy={c} r={20} fill="hsl(var(--brand))" />
    </svg>
  );
}
